/**
 * TCA Bridge detection and auto-login helpers.
 *
 * Shared between the login page (pre-auth auto-login) and the SessionProvider
 * (post-auth session bridge). The login page uses these to detect TCA presence
 * and attempt auto-login before showing the login form.
 *
 * Phase 16: TCA Auto-Login for Managed Workstations
 */

const BRIDGE_BASE_URL = 'http://127.0.0.1:9797';

export interface TcaBridgeHealth {
  status: string;
  version: string;
}

export interface TcaAutoLoginEligibility {
  eligible: boolean;
  /** machine reason when eligible=false — e.g. "wrong_account",
   *  "screen_locked", "device_not_enrolled", "no_active_console_user". */
  reason?: string;
  device_id: string;
  /** Windows username of the active console session. */
  username: string;
  screen_locked?: boolean;
  /** Agent-managed local usernames on this device. */
  managed_usernames?: string[];
  /** True when the console user IS one of the managed accounts. */
  console_user_managed?: boolean;
  /** True when the console user holds an active portal binding —
   *  managed-but-unbound accounts can't auto-login (no portal user). */
  console_user_bound?: boolean;
  /** True while the user still needs to switch to the managed account. */
  pending_switch?: boolean;
  timestamp?: string;
}

export interface TcaAutoLoginSignature {
  device_id: string;
  nonce: string;
  timestamp: string;
  signature: string;
}

/**
 * Check if the TCA bridge is running on localhost.
 */
export async function probeTcaBridge(signal?: AbortSignal): Promise<boolean> {
  try {
    const response = await fetch(`${BRIDGE_BASE_URL}/health`, {
      method: 'GET',
      credentials: 'omit',
      signal,
    });
    if (!response.ok) return false;
    const data = await response.json().catch(() => null) as TcaBridgeHealth | null;
    return data?.status === 'ok';
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Device enrolment via the local bridge
// ---------------------------------------------------------------------------

export interface TcaEnrolRequest {
  enrollment_token: string;
  user_id?: string;
  client_id?: string;
  role?: string;
  display_name?: string;
  client_name?: string;
  avatar_url?: string;
  email?: string;
}

export interface TcaEnrolResult {
  ok: boolean;
  /** "approved" | "pending_approval" — mirrors the device enrolment status. */
  enrollment_status?: string;
  device_id?: string;
  error?: string;
}

/**
 * Deliver an enrolment token to the TCA agent through its local bridge
 * (POST http://127.0.0.1:9797/api/enrol) instead of relying on the
 * totalaccess://enrol deep link.
 *
 * The deep link is fragile: browsers can drop custom-scheme navigation
 * fired without a user gesture, the "Open app?" prompt can be dismissed,
 * and the scheme may not be registered at all (agent running without the
 * installer). The bridge POST has none of those failure modes, so callers
 * should try it first and only fall back to the deep link when the bridge
 * is unreachable.
 */
export async function enrolDeviceViaBridge(params: TcaEnrolRequest): Promise<TcaEnrolResult> {
  try {
    const response = await fetch(`${BRIDGE_BASE_URL}/api/enrol`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'omit',
      body: JSON.stringify(params),
      // Enrolment can take a while (WireGuard keypair + tunnel bring-up +
      // initial GPO apply) — match the agent's own 30s client timeout.
      signal: AbortSignal.timeout(30000),
    });
    const data = await response.json().catch(() => ({} as Record<string, unknown>));
    if (!response.ok || (data as { success?: boolean }).success !== true) {
      const err = (data as { error?: string; details?: string }).error ||
        (data as { details?: string }).details ||
        `HTTP ${response.status}`;
      return { ok: false, error: err };
    }
    const d = data as { enrollment_status?: string; device_id?: string | number };
    return { ok: true, enrollment_status: d.enrollment_status, device_id: d.device_id != null ? String(d.device_id) : undefined };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : 'Bridge unreachable' };
  }
}

/**
 * Check auto-login eligibility from the TCA bridge.
 * Returns the device ID and Windows username if eligible.
 */
export async function checkAutoLoginEligibility(signal?: AbortSignal): Promise<TcaAutoLoginEligibility | null> {
  try {
    const response = await fetch(`${BRIDGE_BASE_URL}/api/auto-login-eligibility`, {
      method: 'GET',
      credentials: 'omit',
      signal,
    });
    if (!response.ok) return null;
    return await response.json() as TcaAutoLoginEligibility;
  } catch {
    return null;
  }
}

/**
 * Get a signed nonce + timestamp from the TCA bridge for auto-login.
 */
export async function getAutoLoginSignature(signal?: AbortSignal): Promise<TcaAutoLoginSignature | null> {
  try {
    const response = await fetch(`${BRIDGE_BASE_URL}/api/auto-login-signature`, {
      method: 'GET',
      credentials: 'omit',
      signal,
    });
    if (!response.ok) return null;
    return await response.json() as TcaAutoLoginSignature;
  } catch {
    return null;
  }
}

/**
 * Attempt TCA auto-login by calling the portal endpoint with the signed
 * nonce + timestamp + device info from the bridge.
 */
export interface TcaAutoLoginResult {
  success: boolean;
  error?: string;
  data?: {
    user: {
      id: number;
      email: string;
      first_name: string | null;
      last_name: string | null;
      display_name: string | null;
      role: string;
      client_id: number;
      employee_id: number | null;
    };
    tokens: {
      access_token: string;
      refresh_token: string;
      session_token: string;
    };
    session: {
      id: string;
      expires_at: string;
      platform: string;
    };
    message?: string;
  };
}

export async function attemptTcaAutoLogin(
  eligibility: TcaAutoLoginEligibility,
  signature: TcaAutoLoginSignature
): Promise<TcaAutoLoginResult> {
  try {
    const response = await fetch('/api/v1/auth/tca-auto-login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        device_id: eligibility.device_id,
        windows_username: eligibility.username,
        nonce: signature.nonce,
        timestamp: signature.timestamp,
        signature: signature.signature,
      }),
    });

    const data = await response.json();
    if (!response.ok || !data.success) {
      return { success: false, error: data.error || 'Auto-login failed' };
    }

    return { success: true, data };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'Network error' };
  }
}

// ---------------------------------------------------------------------------
// Phase 10.3: Remote Support session helpers
// ---------------------------------------------------------------------------

export interface RemoteSupportStatus {
  active: boolean;
  room_name: string;
  permissions: string[];
  started_at: string;
  expires_at: string;
}

/**
 * Start a remote support session on the TCA agent. The agent joins the
 * LiveKit room and begins screen capture.
 */
export async function startRemoteSupport(
  roomName: string,
  token: string,
  livekitHost: string,
): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch(`${BRIDGE_BASE_URL}/support/remote/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'omit',
      body: JSON.stringify({ room_name: roomName, token, livekit_host: livekitHost }),
    });
    const data = await response.json();
    if (!response.ok) return { success: false, error: data.error || 'Failed to start' };
    return { success: true };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'Network error' };
  }
}

/**
 * Stop the remote support session on the TCA agent.
 */
export async function stopRemoteSupport(): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch(`${BRIDGE_BASE_URL}/support/remote/stop`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'omit',
    });
    const data = await response.json();
    if (!response.ok) return { success: false, error: data.error || 'Failed to stop' };
    return { success: true };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'Network error' };
  }
}

/**
 * Get the current remote support session status from the TCA agent.
 */
export async function getRemoteSupportStatus(): Promise<RemoteSupportStatus | null> {
  try {
    const response = await fetch(`${BRIDGE_BASE_URL}/support/remote/status`, {
      method: 'GET',
      credentials: 'omit',
    });
    if (!response.ok) return null;
    return await response.json() as RemoteSupportStatus;
  } catch {
    return null;
  }
}

/**
 * Grant remote control permissions on the TCA agent.
 */
export async function grantRemoteControl(
  permissions: string[],
): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch(`${BRIDGE_BASE_URL}/support/remote/grant`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'omit',
      body: JSON.stringify({ permissions }),
    });
    const data = await response.json();
    if (!response.ok) return { success: false, error: data.error || 'Failed to grant' };
    return { success: true };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'Network error' };
  }
}

/**
 * Revoke remote control permissions on the TCA agent.
 */
export async function revokeRemoteControl(): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch(`${BRIDGE_BASE_URL}/support/remote/revoke`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'omit',
    });
    const data = await response.json();
    if (!response.ok) return { success: false, error: data.error || 'Failed to revoke' };
    return { success: true };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'Network error' };
  }
}

// ---------------------------------------------------------------------------
// Device page helpers — live agent state + local actions via the bridge.
// Used by /client-zone/my-device. All calls fail soft (null / {ok:false})
// so the page degrades gracefully when the agent isn't running.
// ---------------------------------------------------------------------------

export interface TcaWireGuardStatus {
  active: boolean;
  /** "native" | "external" | "netstack" | "off" */
  mode: string;
  localAddr: string;
  peerEndpoint: string;
  dnsAddr: string;
  /** True when a handshake completed within the last ~3 minutes. */
  connected: boolean;
  lastHandshakeAt: number;
  rxBytes: number;
  txBytes: number;
}

export interface TcaAdStatus {
  domainJoined: boolean;
  domain: string;
  hostname: string;
  wireguardActive: boolean;
  platform: string;
  windowsEdition: string;
  canDomainJoin: boolean;
  dnsServer: string;
  gpoEnabled: boolean;
}

/** Shape of GET /status on the bridge. */
export interface TcaAgentStatus {
  version?: string;
  bridgePort?: number;
  worktracker?: boolean;
  syncPaused?: boolean;
  syncing?: boolean;
  enrolled?: boolean;
  pending_approval?: boolean;
  device_id?: string;
  ad?: TcaAdStatus;
  wireguard?: TcaWireGuardStatus;
  account_context?: {
    console_username?: string;
    managed_usernames?: string[];
    console_user_managed?: boolean;
    pending_switch?: boolean;
    /** RFC3339 expiry while the console user holds a JIT admin grant. */
    temp_admin_expires_at?: string;
  };
}

export interface TcaSyncConflict {
  file_id: string;
  file_path: string;
  local_modified: string;
  server_modified: string;
  local_size: number;
  server_size: number;
  drive_id?: string;
  local_path?: string;
}

/** Shape of GET /sync/status on the bridge. */
export interface TcaSyncStatus {
  syncing: boolean;
  paused: boolean;
  lastSync: string | null;
  pendingCount: number;
  errors: string[];
  conflicts?: TcaSyncConflict[];
}

/** Shape of GET /sync/config on the bridge. */
export interface TcaSyncConfig {
  rootFolder?: string;
  [key: string]: unknown;
}

/** Shape of GET /tamper/status on the bridge. */
export interface TcaTamperStatus {
  enabled: boolean;
  service_watchdog: boolean;
  process_watchdog: boolean;
  require_uninstall_code: boolean;
  service_running: boolean;
  last_service_check?: string;
  recent_events?: Array<{ timestamp: string; type: string; details: string; action: string }>;
}

/** Shape of GET /update/status on the bridge. */
export interface TcaUpdateStatus {
  enabled: boolean;
  source_url?: string;
  check_interval?: number;
  current_version?: string;
  signature_verify?: boolean;
  update_available?: boolean;
  latest_version?: string;
  release_notes?: string;
  last_check_at?: number;
  last_error?: string;
  preparing?: boolean;
  prepare_progress?: number;
  prepared_version?: string;
}

/** Shape of GET /api/current-user on the bridge. */
export interface TcaCurrentUser {
  username: string;
  device_id: string;
  enrolled: boolean;
  platform: string;
  timestamp: string;
}

async function tcaGet<T>(path: string, signal?: AbortSignal): Promise<T | null> {
  try {
    const response = await fetch(`${BRIDGE_BASE_URL}${path}`, {
      method: 'GET',
      credentials: 'omit',
      signal,
    });
    if (!response.ok) return null;
    return await response.json() as T;
  } catch {
    return null;
  }
}

async function tcaPost(path: string): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch(`${BRIDGE_BASE_URL}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'omit',
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) return { success: false, error: (data as { error?: string }).error || `HTTP ${response.status}` };
    return { success: true };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'Network error' };
  }
}

export const getTcaAgentStatus = (signal?: AbortSignal) => tcaGet<TcaAgentStatus>('/status', signal);
export const getTcaSyncStatus = (signal?: AbortSignal) => tcaGet<TcaSyncStatus>('/sync/status', signal);
export const getTcaSyncConfig = (signal?: AbortSignal) => tcaGet<TcaSyncConfig>('/sync/config', signal);
export const getTcaTamperStatus = (signal?: AbortSignal) => tcaGet<TcaTamperStatus>('/tamper/status', signal);
export const getTcaUpdateStatus = (signal?: AbortSignal) => tcaGet<TcaUpdateStatus>('/update/status', signal);
export const getTcaCurrentUser = (signal?: AbortSignal) => tcaGet<TcaCurrentUser>('/api/current-user', signal);

export const tcaSyncNow = () => tcaPost('/sync/now');
export const tcaPauseSync = () => tcaPost('/sync/pause');
export const tcaResumeSync = () => tcaPost('/sync/resume');
export const tcaTriggerUpdateCheck = () => tcaPost('/update/check');
export const tcaPrepareUpdate = () => tcaPost('/update/prepare');
/** Disconnect the console session ("Switch user") — only honoured by the
 *  bridge while an account switch is genuinely pending. */
export const tcaSwitchUser = () => tcaPost('/api/switch-user');
export const tcaEndAdminSession = () => tcaPost('/api/temp-admin/revoke');
